/**
 * Event content for a self-schedule picked slot. Time label top-left
 * (ellipsised if the pill is narrow) with a × pinned to the
 * top-right so it is visible regardless of slot height or label
 * width.
 */
import { Box, Flex, IconButton } from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import moment from 'moment-timezone';
import { KText } from '@/ui-library/components/text';

interface SlotPillProps {
  slotId: string;
  startIso: string;
  endIso: string;
  timezone: string;
  /** Origin — forwarded to the reducer so it knows whether to stash
   *  the id in `droppedIds` or drop from `customPicks`. */
  isGenerated: boolean;
  onRemove: (_slotId: string, _isGenerated: boolean) => void;
}

export function SlotPill({
  slotId,
  startIso,
  endIso,
  timezone,
  isGenerated,
  onRemove,
}: SlotPillProps) {
  const label = moment.tz(startIso, timezone).format('hh:mm A');
  const endLabel = moment.tz(endIso, timezone).format('hh:mm A');

  return (
    <Box
      position="relative"
      h="100%"
      w="100%"
      px="6px"
      pt="4px"
      overflow="hidden"
    >
      {/* Label sits flush-left; right padding reserves space so the
          × never sits on top of the text. Ellipsis kicks in when the
          pill is narrower than the formatted time. */}
      <Flex align="flex-start" pr="18px">
        <KText
          fontSize="11px"
          fontWeight="600"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          lineHeight="16px"
          w="100%"
        >
          {label}
        </KText>
      </Flex>
      {/* × pinned top-right so short (15-min) pills still show it. */}
      <IconButton
        aria-label={`Remove slot ${label}–${endLabel}`}
        icon={<CloseIcon boxSize="7px" />}
        size="xs"
        variant="ghost"
        minW="14px"
        h="14px"
        p={0}
        position="absolute"
        top="4px"
        right="4px"
        color="primary-700"
        _hover={{ bg: 'primary-100' }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove(slotId, isGenerated);
        }}
      />
    </Box>
  );
}
